import React, { useEffect, useState } from 'react';
import { RING, fillFor } from '../../wall/wallTokens';
import { T, RADIUS, BODY_FONT } from '../messagesModel';
import { LOOK_BACK, lookBackReading, nextLookBack } from './planModel';
import { safeBottom } from '../../../utils/safeArea';

/**
 * FIND A PLAN (handoff §1 "manual run"): `[ FIND A PLAN ]`, LOOK BACK with LAST 20 MSGS · LAST
 * HOUR · TODAY, and FIND THE PLAN, which reads `READING THE LAST 20 MESSAGES…` while it runs.
 * Found: the popover closes and the bar plays its arrival labelled PLAN FOUND. Nothing: NO PLAN
 * FOUND IN …, with LOOK FURTHER BACK and START ONE ANYWAY.
 *
 *   onRun(range) → Promise<'found'|'none'|'error'>   found closes the popover (the caller owns the bar); error says TRY AGAIN
 *   onStartAnyway()                   opens the plan sheet with an empty draft (the caller decides)
 */
export default function FindPlanPopover({ open, onClose, onRun, onStartAnyway, anchor = 'bar' }) {
  const [range, setRange] = useState('messages');
  const [state, setState] = useState('idle'); // idle | scanning | none | error
  useEffect(() => { if (open) setState('idle'); }, [open]);
  if (!open) return null;

  const run = async (r = range) => {
    setState('scanning');
    const result = await onRun?.(r);
    if (result === true || result === 'found') { onClose?.(); return; }
    setState(result === 'error' ? 'error' : 'none');
  };
  const further = () => {
    const nx = nextLookBack(range);
    if (!nx) { setState('none'); return; }
    setRange(nx);
    run(nx);
  };

  const panel = {
    position: 'absolute', zIndex: 36, width: 380, maxWidth: 'calc(100% - 24px)', borderRadius: 20, border: '2px solid transparent',
    background: `linear-gradient(${T.white},${T.white}) padding-box, ${RING} border-box`, padding: 18, display: 'flex', flexDirection: 'column', gap: 12, boxSizing: 'border-box',
    ...(anchor === 'menu' ? { right: 12, top: 56 } : { left: 12, bottom: safeBottom(72) }),
  };

  return (
    <>
      <div data-plan-find-scrim onClick={onClose} style={{ position: 'absolute', inset: 0, zIndex: 35 }} />
      <div data-plan-find role="dialog" aria-label="Find a plan" className="font-mono" style={panel}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em' }}>[ FIND A PLAN ]</span>
          <span style={{ fontSize: 9, letterSpacing: '.12em', color: T.grey400 }}>ONLY YOU SEE THIS</span>
        </div>
        <span style={{ fontFamily: BODY_FONT, fontSize: 13, lineHeight: 1.45, color: T.grey600 }}>Reads recent messages in this chat and drafts a Portal or an Event from them.</span>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.12em', marginTop: 2 }}>LOOK BACK</span>
        <div role="radiogroup" aria-label="Look back" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 4, padding: 4, borderRadius: RADIUS.pill, background: T.off }}>
          {LOOK_BACK.map((r) => {
            const on = range === r.key;
            return (
              <button key={r.key} type="button" role="radio" aria-checked={on} onClick={() => { setRange(r.key); setState('idle'); }} style={{ height: 30, borderRadius: RADIUS.pill, border: '2px solid transparent', fontSize: 9, fontWeight: 700, letterSpacing: '.08em', whiteSpace: 'nowrap', background: on ? fillFor('on') : 'transparent', color: on ? T.white : T.ink, cursor: 'pointer', fontFamily: 'inherit' }}>{r.label}</button>
            );
          })}
        </div>
        {state === 'idle' && (
          <button type="button" data-plan-find-run onClick={() => run()} className="hover:bg-white hover:text-black" style={{ height: 44, borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, background: T.ink, color: T.white, fontSize: 11, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer', fontFamily: 'inherit', transition: 'background-color 200ms, color 200ms' }}>FIND THE PLAN</button>
        )}
        {state === 'scanning' && (
          <div data-plan-find-scanning aria-live="polite" style={{ height: 44, borderRadius: RADIUS.pill, border: '2px solid transparent', background: `linear-gradient(${T.ink},${T.ink}) padding-box, ${RING} border-box`, color: T.white, fontSize: 10, fontWeight: 700, letterSpacing: '.12em', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            READING {lookBackReading(range)}…
          </div>
        )}
        {state === 'error' && (
          <div data-plan-find-error style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.1em', color: T.danger }}>COULDN’T READ THE CHAT. NOTHING WAS CHANGED.</span>
            <button type="button" onClick={() => run()} style={{ height: 38, borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, background: T.ink, color: T.white, fontSize: 10, fontWeight: 700, letterSpacing: '.1em', cursor: 'pointer', fontFamily: 'inherit' }}>TRY AGAIN</button>
          </div>
        )}
        {state === 'none' && (
          <div data-plan-find-none style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.1em', color: T.grey500 }}>NO PLAN FOUND IN {lookBackReading(range)}.</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={further} disabled={!nextLookBack(range)} style={{ flex: 1, height: 38, borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, background: T.white, fontSize: 10, fontWeight: 700, letterSpacing: '.1em', cursor: 'pointer', fontFamily: 'inherit', opacity: nextLookBack(range) ? 1 : 0.4 }}>LOOK FURTHER BACK</button>
              <button type="button" onClick={() => { onClose?.(); onStartAnyway?.(); }} style={{ flex: 1, height: 38, borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, background: T.ink, color: T.white, fontSize: 10, fontWeight: 700, letterSpacing: '.1em', cursor: 'pointer', fontFamily: 'inherit' }}>START ONE ANYWAY</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
